import React from 'react';
import { generatePath } from 'react-router-dom';
import { Body } from '@sato/components';
import { orderBy } from 'lodash';
import { observer } from 'mobx-react-lite';

import { Link } from '@components/common/Link';
import { MessagePreview } from '@components/MessagePreview';
import { SkeletonList } from '@components/Skeleton/SkeletonList';
import { useIncidents } from '@hooks/incidents';
import { useNoticesForRealEstate } from '@hooks/useNoticesForRealEstate';
import { Incident } from '@models/Incident';
import { Notice } from '@models/notices.types';
import { ROUTES } from '@routes/routes.constants';
import { apartmentStore } from '@stores/apartmentStore';
import { t } from '@stores/localization';
import { getIncidentIcon } from '@utils/incidents.utils';

import { getMessageStatusChipProps } from './MessagePreview/MessagePreview.utils';

export interface MessageListProps {
  limit?: number;
  /**
   * Determines what type of messages are listed.
   *
   * @default "all"
   */
  filter?: MessageItemFilter;
}

export type MessageItemFilter = 'incidents' | 'notices' | 'all';

type MessageItem =
  | { kind: 'incident'; incident: Incident }
  | { kind: 'notice'; notice: Notice };

export const MessageList = observer(
  ({ limit, filter = 'all' }: MessageListProps) => {
    const showIncidents = filter == 'incidents' || filter == 'all';
    const showNotices = filter == 'notices' || filter == 'all';

    const incidentsQuery = useIncidents({ disabled: !showIncidents });

    const noticesQuery = useNoticesForRealEstate(
      apartmentStore.selectedApartmentRealestateId,
      { disabled: !showNotices }
    );

    if (incidentsQuery.isLoading || noticesQuery.isLoading) {
      return (
        <SkeletonList
          wrapperComponent="ul"
          itemCount={limit}
          className="my-3 gap-3"
          itemClassName="rounded-sm h-[114px]"
        />
      );
    }

    const incidents = showIncidents ? (incidentsQuery?.incidents ?? []) : [];
    const notices = showNotices ? (noticesQuery?.data ?? []) : [];

    const incidentsAndNotices = [
      ...incidents.map<MessageItem>((incident) => ({
        kind: 'incident',
        incident,
      })),
      ...notices.map<MessageItem>((notice) => ({
        kind: 'notice',
        notice,
      })),
    ];

    const incidentsAndNoticesSorted = orderBy(
      incidentsAndNotices,
      (incidentOrNotice) => {
        if (incidentOrNotice.kind === 'incident') {
          return new Date(incidentOrNotice.incident.createdon);
        }
        return new Date(incidentOrNotice.notice.startDateTime);
      },
      'desc'
    );

    if (!incidentsAndNoticesSorted.length) {
      return (
        <div className="flex min-h-[15rem] w-full items-center justify-center rounded-sm border md:rounded-md">
          <Body className="text-brand-other-black-medium self-center">
            {t('no_messages_available')}
          </Body>
        </div>
      );
    }

    return (
      <ul className="w-full">
        {incidentsAndNoticesSorted.slice(0, limit).map((incidentOrNotice) => (
          <IncidentOrNoticeItem
            incidentOrNotice={incidentOrNotice}
            key={
              incidentOrNotice.kind === 'incident'
                ? incidentOrNotice.incident.incidentId
                : incidentOrNotice.notice.noticeId
            }
          />
        ))}
      </ul>
    );
  }
);

const IncidentOrNoticeItem = observer(
  ({ incidentOrNotice }: { incidentOrNotice: MessageItem }) => (
    <li className="border-b border-black-light py-3">
      {incidentOrNotice.kind === 'incident' ? (
        // Incident
        <MessagePreview
          title={incidentOrNotice.incident.title}
          icon={getIncidentIcon(incidentOrNotice.incident)}
          statusChip={getMessageStatusChipProps(
            incidentOrNotice.incident.statusCode,
            t
          )}
          timestamp={incidentOrNotice.incident.createdon}
          wrapperComponent={Link}
          to={generatePath(ROUTES.SINGLE_MESSAGE_THREAD, {
            messageId: incidentOrNotice.incident.incidentId,
          })}
          isLink
        />
      ) : (
        // Notice
        <MessagePreview
          title={incidentOrNotice.notice.subject}
          icon="announcement"
          timestamp={incidentOrNotice.notice.startDateTime}
          wrapperComponent={Link}
          to={generatePath(ROUTES.NOTICE_PAGE, {
            noticeId: incidentOrNotice.notice.noticeId,
          })}
          statusChip={{
            children: t('residentNotice'),
            variant: 'filled',
            className: 'bg-brand-secondary-ultralight !text-black',
          }}
          isLink
        />
      )}
    </li>
  )
);
